import { http, notify } from 'utils'
import {concat, findIndex, pick, set} from 'lodash'

import {
  SET_NOTIFICATIONS, ADD_NOTIFICATIONS, SET_NOTIFICATIONS_PAGINATION, SET_NOTIFICATION_UNREAD_COUNT, SET_NOTIFICATION_PROPERTY
} from 'vx/mutations'

// Message state.
const state = {
  pagination: {},
  notifications: [],
  notification_unread_count: 0,
  linkTypeLookup: {
    requests: 'requests.show',
    bookings: 'bookings.show',
    payment: 'bookings.show.payment',
    vetting: 'vetting.show',
    threads: 'threads.index',
    'kickoffs-with-pickstar.opt-in': 'kickoffs-with-pickstar.opt-in',
    'video-campaign.opt-in': 'video-campaign.opt-in',
    'video-shoutouts.opt-in': 'video-campaign.opt-in'
  }
}

// Message state getters.
const getters = {
  /**
   * Determine if we have no more notifications to load.
   *
   * @return bool
   */
  haveNoMoreNotifications: (state) => {
    return state.pagination.current_page >= state.pagination.last_page ||
      state.pagination.total === 0
  }
}

const actions = {
  /**
   * Get notifications.
   *
   * @return Promise
   */
  getNotifications ({ commit }, params = {}) {
    return http.get('notifications', { params })
      .then((response) => {
        commit(SET_NOTIFICATIONS, response.data.data)
        commit(SET_NOTIFICATIONS_PAGINATION, response.data.meta)
      })
  },

  /**
   * Get the count of unread notifications
   *
   * @param commit
   * @param params
   * @returns {*}
   */
  getUnreadCount ({ commit }, params = {}) {
    return http.get('notifications/unread_count', { params })
      .then((response) => {
        commit(SET_NOTIFICATION_UNREAD_COUNT, response.data)
      })
  },

  /**
   * Notify a user of a new notification and add it to state
   *
   * @param commit
   * @param notification
   */
  handleNotifications ({ commit, state }, notification) {
    notify.systemNotif(notification.heading, { name: state.linkTypeLookup[notification.link_type], params: { id: notification.link_id } })
    commit(ADD_NOTIFICATIONS, notification)
  },

  /**
   * Mark all notifications as read
   * @param commit
   * @param params
   * @returns {Promise<any>}
   */
  readNotifications ({ commit }, params = {}) {
    return http.put(`notifications/read`)
      .then((response) => {
        commit(SET_NOTIFICATION_UNREAD_COUNT, 0)
      })
  },

  /**
   * Get next set of paginated notifications.
   *
   * @return Promise
   */
  getNextNotifications ({ commit, state, getters }, params = {}) {
    if (getters.haveNoMoreNotifications) {
      return Promise.reject(new Error('There are no more threads to load.'))
    }

    params.page = state.pagination.current_page + 1

    return http.get(`notifications`, { params })
      .then((response) => {
        commit(ADD_NOTIFICATIONS, response.data.data)
        commit(SET_NOTIFICATIONS_PAGINATION, response.data.meta)

        return response
      })
  },

  respondToNotification ({ commit }, { userId, responseId, notificationId }) {
    return http.put(`/custom_notification/${userId}/response/${responseId}`).then(response => {
      commit(SET_NOTIFICATION_PROPERTY, {notificationId, property: 'response_id', value: response.data.response_id})
      return response
    })
  }
}

// Message state mutations.
const mutations = {
  [SET_NOTIFICATIONS] (state, notifications) {
    state.notifications = notifications
  },

  [SET_NOTIFICATIONS_PAGINATION] (state, pagination) {
    state.pagination = pick(pagination, ['current_page', 'per_page', 'total', 'last_page'])
  },

  [SET_NOTIFICATION_UNREAD_COUNT] (state, count) {
    state.notification_unread_count = count < 0 ? 0 : count
  },

  [ADD_NOTIFICATIONS] (state, notifications) {
    state.notifications = concat(state.notifications, notifications)
  },

  [SET_NOTIFICATION_PROPERTY] (state, { notificationId, property, value }) {
    let index = findIndex(state.notifications, notification => notification.notification && notification.notification.id === notificationId)

    if (index !== -1) {
      set(state.notifications[index], property, value)
    }
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, state, getters, actions, mutations
}
